// ReportedReviewsDashboard.tsx
import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AppLayoutContext } from '../../providers/AppLayout'
import { useAdminAccess } from '../../providers/AdminAccessContext'
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Tooltip,
  Paper,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import RateReviewIcon from '@mui/icons-material/RateReview'
import EmailIcon from '@mui/icons-material/Email'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ForumIcon from '@mui/icons-material/Forum'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import HandshakeIcon from '@mui/icons-material/Handshake'
import StarRating from '../../components/StarRating/StarRating.component'
import DateRangePicker, { DateRange } from '../../components/DateRangePicker/DateRangePicker.component'
import { ReportedReviewByAdminService, ReportedReview } from '../../services/ReportedReview/GetReportedReviewsService'
import { ReviewService } from '../../services/ReportedReview/RejectReviewService'
import { AcceptReviewService } from '../../services/ReportedReview/AcceptReviewService'
import { AcceptedReviewsService } from '../../services/ReportedReview/GetAcceptedReviewsService'
import { RejectedReviewsService } from '../../services/ReportedReview/GetRejectedReviewsService'
import { ReportStatusTypes } from '../../models/ReportStatus.model'

type StatusTab = 'reported' | 'accepted' | 'rejected'
type CategoryTab = 'all' | 'hospital' | 'agency'

// ── Status config ──
const STATUS_CONFIG: Record<string, { label: string; color: string; gradient: string; icon: JSX.Element; bgLight: string }> = {
  reported: {
    label: 'Reported',
    color: '#FF7043',
    gradient: 'linear-gradient(135deg, #FF7043 0%, #FFB74D 100%)',
    icon: <WarningAmberIcon />,
    bgLight: '#FFF3E0',
  },
  accepted: {
    label: 'Accepted',
    color: '#00C853',
    gradient: 'linear-gradient(135deg, #00C853 0%, #69F0AE 100%)',
    icon: <CheckCircleIcon />,
    bgLight: '#E8F5E9',
  },
  rejected: {
    label: 'Dismissed',
    color: '#FF1744',
    gradient: 'linear-gradient(135deg, #FF1744 0%, #FF8A80 100%)',
    icon: <CancelOutlinedIcon />,
    bgLight: '#FFEBEE',
  },
}

const ReportedReviewsDashboard = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAdmin, isLoading: isAdminLoading } = useAdminAccess()
  const [statusTab, setStatusTab] = useState<StatusTab>('reported')
  const [categoryTab, setCategoryTab] = useState<CategoryTab>('all')
  const [reportedReviews, setReportedReviews] = useState<ReportedReview[]>([])
  const [acceptedReviews, setAcceptedReviews] = useState<ReportedReview[]>([])
  const [rejectedReviews, setRejectedReviews] = useState<ReportedReview[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [actionLoading, setActionLoading] = useState<{ reviewId: number; action: 'approve' | 'dismiss' } | null>(null)
  const [selectedReview, setSelectedReview] = useState<ReportedReview | null>(null)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [dateRange, setDateRange] = useState<DateRange>({ start: '', end: '' })
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLButtonElement | null>(null)
  const [highlightedReviewId, setHighlightedReviewId] = useState<number | null>(null)
  const reviewRefs = useRef<{ [key: number]: HTMLDivElement | null }>({})
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    AppLayoutContext.setPageName('Review Management')
    if (isAdmin) {
      fetchAllReviews()
    }
  }, [isAdmin])

  // Handle scroll to review from notification
  useEffect(() => {
    const reviewIdParam = searchParams.get('reviewId')
    if (reviewIdParam && !loading) {
      const reviewId = parseInt(reviewIdParam, 10)
      if (!isNaN(reviewId)) {
        setTimeout(() => {
          scrollToReview(reviewId)
        }, 500)
      }
    }
  }, [searchParams, loading, reportedReviews, acceptedReviews, rejectedReviews])

  const scrollToReview = (reviewId: number) => {
    const allReviews = [...reportedReviews, ...acceptedReviews, ...rejectedReviews]
    const review = allReviews.find((r) => r.id === reviewId)

    if (review) {
      if (reportedReviews.find((r) => r.id === reviewId)) {
        setStatusTab('reported')
      } else if (acceptedReviews.find((r) => r.id === reviewId)) {
        setStatusTab('accepted')
      } else if (rejectedReviews.find((r) => r.id === reviewId)) {
        setStatusTab('rejected')
      }

      if (review.category) {
        setCategoryTab(review.category)
      }

      setTimeout(() => {
        const element = reviewRefs.current[reviewId]
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setHighlightedReviewId(reviewId)
          setTimeout(() => {
            setHighlightedReviewId(null)
            searchParams.delete('reviewId')
            navigate(`${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, {
              replace: true,
            })
          }, 4000)
        }
      }, 300)
    }
  }

  const isHospitalQuestion = (question: string): boolean => {
    return question.toLowerCase().includes('which hospital are you going to review')
  }

  const isAgencyQuestion = (question: string): boolean => {
    return (
      question.toLowerCase().includes('which agency are you going to review') ||
      question.toLowerCase().includes('which travel nursing agency')
    )
  }

  const isFeedbackQuestion = (question: string): boolean => {
    const lowerQuestion = question.toLowerCase()
    return lowerQuestion.startsWith('tell us about anything we missed') || lowerQuestion === 'additional feedback'
  }

  const determineCategory = (review: ReportedReview): 'hospital' | 'agency' => {
    if (review.organizationType) return review.organizationType
    if (review.category) return review.category
    const hasHospitalQuestion = review.answers?.some((answer) => isHospitalQuestion(answer.question))
    const hasAgencyQuestion = review.answers?.some((answer) => isAgencyQuestion(answer.question))
    if (hasHospitalQuestion) return 'hospital'
    if (hasAgencyQuestion) return 'agency'
    return 'hospital'
  }

  const fetchAllReviews = async () => {
    setLoading(true)
    try {
      const [allReported, accepted, rejected] = await Promise.all([
        new ReportedReviewByAdminService().getReportedReviews().catch(() => []),
        new AcceptedReviewsService().getAcceptedReviews().catch(() => []),
        new RejectedReviewsService().getRejectedReviews().catch(() => []),
      ])

      const reportedArray: ReportedReview[] = (Array.isArray(allReported) ? allReported : []).map((review) => ({
        ...review,
        category: review.category || determineCategory(review),
      }))
      const acceptedArray: ReportedReview[] = (Array.isArray(accepted) ? accepted : []).map((review) => ({
        ...review,
        category: review.category || determineCategory(review),
      }))
      const rejectedArray: ReportedReview[] = (Array.isArray(rejected) ? rejected : []).map((review) => ({
        ...review,
        category: review.category || determineCategory(review),
      }))

      setReportedReviews(reportedArray)
      setAcceptedReviews(acceptedArray)
      setRejectedReviews(rejectedArray)
    } catch (err) {
      console.error('Failed to fetch reviews:', err)
    } finally {
      setLoading(false)
    }
  }

  const renderQuestionText = (question: string): string => {
    return isFeedbackQuestion(question) ? 'User Feedback Text' : question
  }

  const renderAnswerValue = (answer: any, review?: ReportedReview) => {
    if (answer.type === 'radio') {
      return <StarRating rating={Number(answer.value) || 0} size="small" />
    } else if (isHospitalQuestion(answer.question) || isAgencyQuestion(answer.question)) {
      return review?.organizationName || answer.value
    } else {
      return answer.value
    }
  }

  const matchesSearch = (review: ReportedReview, query: string): boolean => {
    if (!query.trim()) return true
    const lowerQuery = query.toLowerCase()
    if (review.email?.toLowerCase().includes(lowerQuery)) return true
    if (review.organizationName?.toLowerCase().includes(lowerQuery)) return true
    if (review.answers) {
      for (const answer of review.answers) {
        if (answer.question?.toLowerCase().includes(lowerQuery)) return true
        if (String(answer.value)?.toLowerCase().includes(lowerQuery)) return true
      }
    }
    return false
  }

  const matchesDateRange = (review: ReportedReview, start: string, end: string): boolean => {
    if (!start && !end) return true
    const reviewDate = new Date(review.createdAt)
    reviewDate.setHours(0, 0, 0, 0)
    if (start) {
      const startDateObj = new Date(start)
      startDateObj.setHours(0, 0, 0, 0)
      if (reviewDate < startDateObj) return false
    }
    if (end) {
      const endDateObj = new Date(end)
      endDateObj.setHours(23, 59, 59, 999)
      if (reviewDate > endDateObj) return false
    }
    return true
  }

  const getCurrentReviews = (): ReportedReview[] => {
    let reviews: ReportedReview[] = []
    switch (statusTab) {
      case 'reported':
        reviews = reportedReviews
        break
      case 'accepted':
        reviews = acceptedReviews
        break
      case 'rejected':
        reviews = rejectedReviews
        break
    }
    let filteredReviews = reviews
    if (categoryTab !== 'all') {
      filteredReviews = reviews.filter((review) => review.category === categoryTab)
    }
    if (searchQuery.trim()) {
      filteredReviews = filteredReviews.filter((review) => matchesSearch(review, searchQuery))
    }
    filteredReviews = filteredReviews.filter((review) => matchesDateRange(review, dateRange.start, dateRange.end))
    return filteredReviews
  }

  const getFilteredCounts = () => {
    let reported = [...reportedReviews]
    let accepted = [...acceptedReviews]
    let rejected = [...rejectedReviews]

    if (categoryTab === 'hospital') {
      reported = reported.filter((r) => r.category === 'hospital')
      accepted = accepted.filter((r) => r.category === 'hospital')
      rejected = rejected.filter((r) => r.category === 'hospital')
    } else if (categoryTab === 'agency') {
      reported = reported.filter((r) => r.category === 'agency')
      accepted = accepted.filter((r) => r.category === 'agency')
      rejected = rejected.filter((r) => r.category === 'agency')
    }

    if (dateRange.start || dateRange.end) {
      reported = reported.filter((r) => matchesDateRange(r, dateRange.start, dateRange.end))
      accepted = accepted.filter((r) => matchesDateRange(r, dateRange.start, dateRange.end))
      rejected = rejected.filter((r) => matchesDateRange(r, dateRange.start, dateRange.end))
    }

    return { reported: reported.length, accepted: accepted.length, rejected: rejected.length }
  }

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })
  const handleOpenDialog = (review: ReportedReview) => {
    setSelectedReview(review)
    setDialogOpen(true)
  }
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedReview(null)
  }

  const handleApprove = async (reviewId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setActionLoading({ reviewId, action: 'approve' })
    try {
      const acceptReviewService = new AcceptReviewService()
      await acceptReviewService.reportReview(reviewId)
      const reviewToMove = reportedReviews.find((r) => r.id === reviewId)
      if (reviewToMove) {
        setReportedReviews(reportedReviews.filter((r) => r.id !== reviewId))
        setAcceptedReviews([...acceptedReviews, reviewToMove])
      }
      setSnackbar({ open: true, message: 'Review approved successfully', severity: 'success' })
      handleCloseDialog()
    } catch (error) {
      console.error(error)
      setSnackbar({ open: true, message: 'Failed to approve review', severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  const handleDismiss = async (reviewId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setActionLoading({ reviewId, action: 'dismiss' })
    try {
      const reviewService = new ReviewService()
      await reviewService.reportReview(reviewId, ReportStatusTypes.REJECTED)
      const reviewToMove = reportedReviews.find((r) => r.id === reviewId)
      if (reviewToMove) {
        setReportedReviews(reportedReviews.filter((r) => r.id !== reviewId))
        setRejectedReviews([...rejectedReviews, reviewToMove])
      }
      setSnackbar({ open: true, message: 'Review dismissed successfully', severity: 'success' })
      handleCloseDialog()
    } catch (error) {
      console.error(error)
      setSnackbar({ open: true, message: 'Failed to dismiss review', severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  // ── Helpers ──
  const totalReviews = reportedReviews.length + acceptedReviews.length + rejectedReviews.length

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    if (diffInSeconds < 3600) {
      const m = Math.floor(diffInSeconds / 60)
      return m <= 0 ? 'Just now' : `${m}m ago`
    }
    if (diffInSeconds < 86400) {
      const h = Math.floor(diffInSeconds / 3600)
      return `${h}h ago`
    }
    if (diffInSeconds < 604800) {
      const d = Math.floor(diffInSeconds / 86400)
      return `${d}d ago`
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Get the entity name (hospital/agency) from a review
  const getEntityName = (review: ReportedReview): string | null => {
    return review.organizationName || null
  }

  // ── Render ──
  const renderMainView = () => {
    const reviews = getCurrentReviews()
    const counts = getFilteredCounts()
    const config = STATUS_CONFIG[statusTab]

    return (
      <Box sx={{ pb: 4 }}>
        {/* ── Banner ── */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            background: 'linear-gradient(135deg, #FF7043 0%, #FF9800 50%, #FFB74D 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', right: -30, top: -30, opacity: 0.07 }}>
            <RateReviewIcon sx={{ fontSize: 240 }} />
          </Box>
          <Box sx={{ position: 'relative' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'flex-start' }} spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  Review Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  Manage and moderate reported reviews across hospitals and agencies
                </Typography>
                <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ForumIcon sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {totalReviews} Total Reviews
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {reportedReviews.length} Awaiting Review
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* ── Category Chips (All / Hospitals / Agencies) ── */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {([
            { key: 'all' as const, label: 'All Reviews', icon: <ForumIcon sx={{ fontSize: 16 }} /> },
            { key: 'hospital' as const, label: 'Hospitals', icon: <LocalHospitalIcon sx={{ fontSize: 16 }} /> },
            { key: 'agency' as const, label: 'Agencies', icon: <HandshakeIcon sx={{ fontSize: 16 }} /> },
          ] as const).map((cat) => {
            const isActive = categoryTab === cat.key
            return (
              <Chip
                key={cat.key}
                icon={React.cloneElement(cat.icon, {
                  sx: { fontSize: '16px !important', color: isActive ? 'white !important' : '#78909C !important' },
                })}
                label={cat.label}
                onClick={() => setCategoryTab(cat.key)}
                sx={{
                  bgcolor: isActive ? '#007C91' : 'transparent',
                  color: isActive ? 'white' : '#546E7A',
                  border: isActive ? 'none' : '1px solid rgba(0,0,0,0.12)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  height: 38,
                  px: 0.5,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '& .MuiChip-icon': { ml: 0.75 },
                  '& .MuiChip-label': { px: 1 },
                  '&:hover': {
                    bgcolor: isActive ? '#006A7A' : 'rgba(0,124,145,0.06)',
                    transform: 'scale(1.02)',
                  },
                }}
              />
            )
          })}
        </Stack>

        {/* ── Status Stat Cards ── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {(['reported', 'accepted', 'rejected'] as const).map((key) => {
            const cfg = STATUS_CONFIG[key]
            const count = counts[key]
            const isActive = statusTab === key
            return (
              <Grid item xs={4} sm={4} key={key}>
                <Card
                  onClick={() => setStatusTab(key)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: { xs: 2.5, sm: 3 },
                    border: isActive ? `2px solid ${cfg.color}` : '1px solid rgba(0,0,0,0.06)',
                    boxShadow: isActive ? `0 4px 20px ${cfg.color}25` : '0 1px 6px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${cfg.color}22`,
                    },
                  }}
                >
                  <Box sx={{ height: 4, background: cfg.gradient }} />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: { xs: '0.55rem', sm: '0.75rem' } }}
                        >
                          {cfg.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: '#1A2027', lineHeight: 1.1, mt: 0.5, fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' } }}>
                          {count}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          width: { xs: 36, sm: 48 },
                          height: { xs: 36, sm: 48 },
                          bgcolor: isActive ? cfg.color : cfg.bgLight,
                          color: isActive ? 'white' : cfg.color,
                          transition: 'all 0.3s ease',
                          display: { xs: 'none', sm: 'flex' },
                        }}
                      >
                        {React.cloneElement(cfg.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>

        {/* ── Search ── */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by email, hospital, agency, feedback…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                '& fieldset': { borderColor: 'rgba(0,124,145,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(0,124,145,0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#007C91' },
              },
            }}
          />
        </Box>

        {/* ── Reviews List ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#FF7043' }} />
          </Box>
        ) : reviews.length === 0 ? (
          <Box
            sx={{
              py: 8,
              px: 4,
              textAlign: 'center',
              background: 'linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 100%)',
              borderRadius: 4,
              border: '2px dashed rgba(0,0,0,0.08)',
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: config.bgLight,
                color: config.color,
                mx: 'auto',
                mb: 2.5,
              }}
            >
              {React.cloneElement(config.icon, { sx: { fontSize: 36 } })}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 1 }}>
              {searchQuery.trim() ? 'No Matching Reviews' : `No ${config.label} Reviews`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#78909C', maxWidth: 420, mx: 'auto' }}>
              {searchQuery.trim()
                ? `No reviews matching "${searchQuery.trim()}" were found.`
                : `There are currently no ${config.label.toLowerCase()} reviews${categoryTab !== 'all' ? ` for ${categoryTab === 'hospital' ? 'hospitals' : 'agencies'}` : ''}.`}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {reviews.map((review) => {
              const feedbackAnswer = review.answers.find((answer) => isFeedbackQuestion(answer.question))
              const isHighlighted = highlightedReviewId === review.id
              const entityName = getEntityName(review)

              return (
                <Grid item xs={12} sm={6} key={review.id}>
                  <Card
                    ref={(el) => {
                      reviewRefs.current[review.id] = el
                    }}
                    onClick={() => handleOpenDialog(review)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      border: isHighlighted
                        ? '2px solid #007C91'
                        : '1px solid rgba(0,0,0,0.06)',
                      borderLeft: isHighlighted
                        ? '4px solid #007C91'
                        : `4px solid ${config.color}`,
                      boxShadow: isHighlighted
                        ? '0 8px 24px rgba(0,124,145,0.4)'
                        : '0 1px 6px rgba(0,0,0,0.04)',
                      bgcolor: isHighlighted ? 'rgba(0,124,145,0.06)' : '#FFFFFF',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden',
                      position: 'relative',
                      animation: isHighlighted ? 'pulse 0.6s ease-in-out' : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.01)' },
                        '100%': { transform: 'scale(1)' },
                      },
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 6px 20px ${config.color}18`,
                        '& .review-actions': { opacity: 1 },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Stack spacing={1.5}>
                        {/* Top row: entity name + actions */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                            {entityName && (
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: '#1A2027',
                                  lineHeight: 1.3,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {entityName}
                              </Typography>
                            )}
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                              <EmailIcon sx={{ fontSize: 14, color: '#90A4AE' }} />
                              <Typography variant="body2" sx={{ color: '#78909C', fontSize: '0.8rem' }}>
                                {review.email}
                              </Typography>
                            </Stack>
                          </Box>

                          {/* Action buttons - reported only */}
                          {statusTab === 'reported' && (
                            <Stack
                              className="review-actions"
                              direction="row"
                              spacing={0.75}
                              sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.25s ease', flexShrink: 0 }}
                            >
                              <Tooltip title="Dismiss" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDismiss(review.id, e)}
                                  disabled={actionLoading?.reviewId === review.id}
                                  sx={{
                                    bgcolor: '#FFEBEE',
                                    color: '#FF1744',
                                    width: 34,
                                    height: 34,
                                    '&:hover': { bgcolor: '#FF1744', color: 'white' },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  {actionLoading?.reviewId === review.id && actionLoading?.action === 'dismiss' ? (
                                    <CircularProgress size={14} sx={{ color: '#FF1744' }} />
                                  ) : (
                                    <CancelIcon sx={{ fontSize: 17 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Approve" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleApprove(review.id, e)}
                                  disabled={actionLoading?.reviewId === review.id}
                                  sx={{
                                    bgcolor: '#E8F5E9',
                                    color: '#00C853',
                                    width: 34,
                                    height: 34,
                                    '&:hover': { bgcolor: '#00C853', color: 'white' },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  {actionLoading?.reviewId === review.id && actionLoading?.action === 'approve' ? (
                                    <CircularProgress size={14} sx={{ color: '#00C853' }} />
                                  ) : (
                                    <CheckIcon sx={{ fontSize: 17 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </Stack>

                        {/* Feedback preview (fixed height for consistent card sizes) */}
                        <Box
                          sx={{
                            bgcolor: '#FAFBFC',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 1,
                            border: '1px solid rgba(0,0,0,0.04)',
                            minHeight: 60, // ensure equal card height regardless of feedback length/presence
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {feedbackAnswer ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#546E7A',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontStyle: 'italic',
                                width: '100%',
                              }}
                            >
                              "{feedbackAnswer.value}"
                            </Typography>
                          ) : null}
                        </Box>

                        {/* Bottom meta */}
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ flexWrap: 'wrap', gap: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={`#${review.id}`}
                            sx={{
                              bgcolor: '#F5F5F5',
                              color: '#78909C',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              '& .MuiChip-icon': { color: '#B0BEC5' },
                            }}
                          />
                          <Chip
                            size="small"
                            label={review.category === 'agency' ? 'Agency' : 'Hospital'}
                            sx={{
                              bgcolor: review.category === 'agency' ? '#E0F7FA' : '#E0F2F1',
                              color: review.category === 'agency' ? '#00838F' : '#00695C',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              height: 22,
                            }}
                          />
                          <Box sx={{ flex: 1 }} />
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CalendarTodayIcon sx={{ fontSize: 13, color: '#B0BEC5' }} />
                            <Typography variant="caption" sx={{ color: '#90A4AE', fontSize: '0.72rem' }}>
                              {formatDate(review.createdAt)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        )}
      </Box>
    )
  }

  if (isAdminLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {renderMainView()}

      {/* ── Detail Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {selectedReview && (
          <>
            {/* Dialog header */}
            <Box
              sx={{
                background: STATUS_CONFIG[statusTab].gradient,
                px: 3,
                py: 2.5,
                color: 'white',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Review #{selectedReview.id}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <EmailIcon sx={{ fontSize: 15, opacity: 0.8 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {selectedReview.email}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarTodayIcon sx={{ fontSize: 15, opacity: 0.8 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {new Date(selectedReview.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
                <IconButton onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  <CloseIcon />
                </IconButton>
              </Stack>

              {/* Meta chips */}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Chip
                  size="small"
                  label={selectedReview.category === 'agency' ? 'Agency Review' : 'Hospital Review'}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                />
                <Chip
                  size="small"
                  label={STATUS_CONFIG[statusTab].label}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Stack>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {selectedReview.answers.length > 0 && (
                <Stack spacing={0} divider={<Divider />}>
                  {selectedReview.answers.map((answer) => {
                    const isFeedback = isFeedbackQuestion(answer.question)
                    return (
                      <Stack
                        key={answer.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        sx={{
                          px: 3,
                          py: 2,
                          bgcolor: isFeedback ? 'rgba(0,124,145,0.03)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
                          transition: 'background 0.15s ease',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#78909C',
                            fontWeight: 500,
                            flex: '0 0 40%',
                            pr: 2,
                            mb: { xs: 0.5, sm: 0 },
                          }}
                        >
                          {renderQuestionText(answer.question)}
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            fontWeight: isFeedback ? 600 : 500,
                            color: isFeedback ? '#007C91' : '#1A2027',
                            fontSize: '0.875rem',
                            wordBreak: 'break-word',
                          }}
                        >
                          {renderAnswerValue(answer, selectedReview)}
                        </Box>
                      </Stack>
                    )
                  })}
                </Stack>
              )}
            </DialogContent>

            {statusTab === 'reported' && selectedReview && (
              <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={
                    actionLoading?.reviewId === selectedReview.id && actionLoading?.action === 'dismiss' ? (
                      <CircularProgress size={16} color="error" />
                    ) : (
                      <CancelIcon />
                    )
                  }
                  onClick={() => handleDismiss(selectedReview.id)}
                  disabled={actionLoading?.reviewId === selectedReview.id}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1, py: 1 }}
                >
                  Dismiss Report
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    actionLoading?.reviewId === selectedReview.id && actionLoading?.action === 'approve' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={() => handleApprove(selectedReview.id)}
                  disabled={actionLoading?.reviewId === selectedReview.id}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1, py: 1, boxShadow: 'none' }}
                >
                  Approve Report
                </Button>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default ReportedReviewsDashboard
